import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { GlassCard } from '@/components/ui/glass-card';

interface WidgetCardProps {
  title: string;
  icon: LucideIcon;
  /** Optional trailing element in the header (e.g. a count badge). */
  action?: ReactNode;
  /** Optional caption shown under the title. */
  caption?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * Shared shell for every dashboard widget. Wraps the app's shadcn `Card`
 * so all widgets share one header treatment — lucide icon + title + optional trailing
 * action + optional caption — matching `features/projects/components/dashboard-tab.tsx`.
 */
export function WidgetCard({ title, icon: Icon, action, caption, className, children }: WidgetCardProps) {
  return (
    <GlassCard interactive className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground transition-transform duration-300 group-hover/glass:-rotate-6 group-hover/glass:scale-110" />
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          {action != null && <div className="ml-auto">{action}</div>}
        </div>
        {caption != null && <p className="text-xs text-muted-foreground">{caption}</p>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </GlassCard>
  );
}
